import React from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection, PartnerAxisTable } from "@/types/dealLaSc.types";
import {
  BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS,
  SUB_GROUP_SHELL_PATH_TO_ADD_SUB_GROUP_SHELL,
} from "@FillInTableContainer/shared/constants/tableCreatorSubGroupShell.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useUpdateEntryData } from "@hooks/useUpdateArticleData";
import { useEntryDataState } from "@stores/entryData";
import { useInitEntryDataValue } from "@stores/initEntryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clickAddMilestoneSubGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => void;
  handleClickToDeleteMilestoneSubGroupShell: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  handleKeyDownToDeleteMilestoneSubGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    subGroupShellIndex: number;
    subGroupShellKey: string | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDealsLicenseeTableSubGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const initEntryData = useInitEntryDataValue(getPressIdFromURL());
  const updateEntryData = useUpdateEntryData();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteGroupShell } = useCheckDisabledDelete();

  const handleAddSubGroupShell = (groupShellInfo: {
    tableIndexParameter: number;
    groupShellIndexParameter: number;
    groupShellKeyParameter: string | null;
    subGroupShellIndexParameter: number;
    subGroupShellKeyParameter: string | null;
  }) => {
    const {
      tableIndexParameter,
      groupShellIndexParameter,
      subGroupShellKeyParameter,
      subGroupShellIndexParameter,
    } = groupShellInfo;

    if (subGroupShellKeyParameter === null) return;

    const targetShell = SUB_GROUP_SHELL_PATH_TO_ADD_SUB_GROUP_SHELL[
      subGroupShellKeyParameter
    ](entryData, tableIndexParameter, groupShellIndexParameter);

    if (targetShell === undefined) return;

    const newArrayLength = targetShell.length + 1 || 1;
    const emptyArray = new Array(newArrayLength).fill({});

    const addMilestoneSubGroupShellData = emptyArray.map((_, index) => {
      const milestoneSubGroupShellData =
        BASIC_DATA_FOR_ADDING_SUB_GROUP_SHELLS[articleSection][
          subGroupShellKeyParameter
        ](initEntryData);

      if (index === subGroupShellIndexParameter) {
        return milestoneSubGroupShellData;
      }

      if (index > subGroupShellIndexParameter) {
        return targetShell[index - 1];
      }

      if (index < subGroupShellIndexParameter) {
        return targetShell[index];
      }

      return targetShell[index];
    });

    const changeSubGroupShellData: Record<string, ArticleEntryData> = {
      "Milestone Payment And Detail": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableData, groupShellInfoIndex) => {
              if (groupShellInfoIndex === groupShellIndexParameter) {
                return {
                  ...tableData,
                  "Milestone Payment And Detail": addMilestoneSubGroupShellData,
                };
              }

              return tableData;
            },
          ),
        },
      } as ArticleEntryData,
      "Other Payment And Detail": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableData, tableDataIndex) => {
              if (tableDataIndex === groupShellIndexParameter) {
                return {
                  ...tableData,
                  "Other Payment And Detail": addMilestoneSubGroupShellData,
                };
              }

              return tableData;
            },
          ),
        },
      } as ArticleEntryData,
      "Royalty(LA&SC)": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableData, tableDataIndex) => {
              if (tableDataIndex === groupShellIndexParameter) {
                return {
                  ...tableData,
                  "Royalty(LA&SC)": addMilestoneSubGroupShellData,
                };
              }

              return tableData;
            },
          ),
        },
      } as ArticleEntryData,
      Advisor: {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Advisor Table": addMilestoneSubGroupShellData,
        },
      } as ArticleEntryData,
      "Partner A": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner A": addMilestoneSubGroupShellData,
          },
        },
      } as ArticleEntryData,
      "Partner B": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner B": addMilestoneSubGroupShellData,
          },
        },
      } as ArticleEntryData,
      "Partner C": {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner C": addMilestoneSubGroupShellData,
          },
        },
      } as ArticleEntryData,
    };
    updateEntryData(changeSubGroupShellData[subGroupShellKeyParameter]);
    setEntryData(changeSubGroupShellData[subGroupShellKeyParameter]);
    changeCurrentHistory({
      entryData: changeSubGroupShellData[subGroupShellKeyParameter],
      changedDataInfo: {
        target: "subGroupShell",
        action: "add",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex,
          subGroupShellKey,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
  };

  const clickAddMilestoneSubGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
      subGroupShellIndexParameter: number;
      subGroupShellKeyParameter: string | null;
    },
  ) => {
    event.stopPropagation();
    handleAddSubGroupShell(groupShellInfo);
  };

  const handleClearSubGroupShell = () => {
    const clearMilestoneSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Milestone Payment And Detail": tableInfo[
                    "Milestone Payment And Detail"
                  ].map((subGroupShellInfo, subGroupShellInfoIndex) => {
                    if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                      return {
                        ...subGroupShellInfo,
                        "Milestone Payment(LA&SC)": {
                          ...subGroupShellInfo["Milestone Payment(LA&SC)"],
                          value: [],
                        },
                        "Milestone Payment Detail(LA&SC)": {
                          ...subGroupShellInfo[
                            "Milestone Payment Detail(LA&SC)"
                          ],
                          value: [],
                        },
                      };
                    }

                    return subGroupShellInfo;
                  }),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearOtherPaymentSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Other Payment And Detail": tableInfo[
                    "Other Payment And Detail"
                  ].map((subGroupShellInfo, subGroupShellInfoIndex) => {
                    if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                      return {
                        ...subGroupShellInfo,
                        "Other Payment(LA&SC)": {
                          ...subGroupShellInfo["Other Payment(LA&SC)"],
                          value: [],
                        },
                        "Other Payment Detail(LA&SC)": {
                          ...subGroupShellInfo["Other Payment Detail(LA&SC)"],
                          value: [],
                        },
                      };
                    }

                    return subGroupShellInfo;
                  }),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearRoyaltySubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Royalty(LA&SC)": tableInfo["Royalty(LA&SC)"].map(
                    (subGroupShellInfo, subGroupShellInfoIndex) => {
                      if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                        return {
                          ...subGroupShellInfo,
                          "Royalty Rate": {
                            ...subGroupShellInfo["Royalty Rate"],
                            value: [],
                          },
                          "Royalty Detail": {
                            ...subGroupShellInfo["Royalty Detail"],
                            value: [],
                          },
                        };
                      }

                      return subGroupShellInfo;
                    },
                  ),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearAdvisorSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Advisor Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Advisor Table"
          ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
            if (subGroupShellInfoIndex === info.subGroupShellIndex) {
              return {
                ...subGroupShellInfo,
                "Advisor Name": {
                  ...subGroupShellInfo["Advisor Name"],
                  value: [],
                },
                "Advisor Role": {
                  ...subGroupShellInfo["Advisor Role"],
                  value: [],
                },
                "Party Axis": {
                  ...subGroupShellInfo["Party Axis"],
                  value: [],
                },
              };
            }
            return subGroupShellInfo;
          }),
        },
      };
    };

    const clearPartnerASubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner A":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner A"
              ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    "Partner A Name": {
                      ...subGroupShellInfo["Partner A Name"],
                      value: [],
                    },
                    "Partner A Relationship Context": {
                      ...subGroupShellInfo["Partner A Relationship Context"],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              }) ?? [],
          },
        },
      };
    };

    const clearPartnerBSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner B":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner B"
              ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    "Partner B Name": {
                      ...subGroupShellInfo["Partner B Name"],
                      value: [],
                    },
                    "Partner B Relationship Context": {
                      ...subGroupShellInfo["Partner B Relationship Context"],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              }) ?? [],
          },
        },
      };
    };

    const clearPartnerCSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner C":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner C"
              ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    "Partner C Name": {
                      ...subGroupShellInfo["Partner C Name"],
                      value: [],
                    },
                    "Partner C Relationship Context": {
                      ...subGroupShellInfo["Partner C Relationship Context"],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              }) ?? [],
          },
        },
      };
    };

    const clearPartnerAxisSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Paying Partner Axis(LA&SC)": {
                    ...tableInfo["Paying Partner Axis(LA&SC)"],
                    value: [],
                  },
                  "Receiving Partner Axis(LA&SC)": {
                    ...tableInfo["Receiving Partner Axis(LA&SC)"],
                    value: [],
                  },
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearUpfrontSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Upfront Payment(LA&SC)": {
                    ...tableInfo["Upfront Payment(LA&SC)"],
                    value: [],
                  },
                  "Royalty(LA&SC)": {
                    ...tableInfo["Royalty(LA&SC)"],
                    value: [],
                  },
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const clearTotalSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Total Payment(LA&SC)": {
                    ...tableInfo["Total Payment(LA&SC)"],
                    value: [],
                  },
                  "Disclosed Payment Sum(LA&SC)": {
                    ...tableInfo["Disclosed Payment Sum(LA&SC)"],
                    value: [],
                  },
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    if (info.subGroupShellKey === "Milestone Payment And Detail") {
      const milestoneLength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
          "Main Financial Terms Table"
        ]?.[info.groupShellIndex || 0]?.["Milestone Payment And Detail"]
          ?.length || 0;

      setEntryData(clearMilestoneSubGroupShell());
      changeCurrentHistory({
        entryData: clearMilestoneSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        milestoneLength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Other Payment And Detail") {
      const otherPaymentAndDetailLength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
          "Main Financial Terms Table"
        ]?.[info.groupShellIndex || 0]?.["Other Payment And Detail"]?.length ||
        0;

      setEntryData(clearOtherPaymentSubGroupShell());
      changeCurrentHistory({
        entryData: clearOtherPaymentSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        otherPaymentAndDetailLength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Royalty(LA&SC)") {
      const royaltyLength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
          "Main Financial Terms Table"
        ]?.[info.groupShellIndex || 0]?.["Royalty(LA&SC)"]?.length || 0;

      setEntryData(clearRoyaltySubGroupShell());
      changeCurrentHistory({
        entryData: clearRoyaltySubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        royaltyLength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Advisor") {
      const advisorLength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Advisor Table"]
          ?.length || 0;

      setEntryData(clearAdvisorSubGroupShell());
      changeCurrentHistory({
        entryData: clearAdvisorSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        advisorLength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Partner A") {
      const partnerALength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
          "Partner A"
        ]?.length || 0;

      setEntryData(clearPartnerASubGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerASubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        partnerALength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Partner B") {
      const partnerALength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
          "Partner B"
        ]?.length || 0;

      setEntryData(clearPartnerBSubGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerBSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        partnerALength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Partner C") {
      const partnerALength =
        entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
          "Partner C"
        ]?.length || 0;

      setEntryData(clearPartnerCSubGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerCSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(
        partnerALength > 1 && !checkDisabledDeleteGroupShell()
          ? {
              type: "delete",
              container: "subGroupShell",
            }
          : DEFAULT_FOCUS_STATUS,
      );
    }

    if (info.subGroupShellKey === "Partner Axis") {
      setEntryData(clearPartnerAxisSubGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerAxisSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.subGroupShellKey === "Upfront") {
      setEntryData(clearUpfrontSubGroupShell());
      changeCurrentHistory({
        entryData: clearUpfrontSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.subGroupShellKey === "Total Payment") {
      setEntryData(clearTotalSubGroupShell());
      changeCurrentHistory({
        entryData: clearTotalSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }
  };

  const handleDeleteSubGroupShell = () => {
    if (checkDisabledDeleteGroupShell()) return;

    const deleteMilestoneSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Milestone Payment And Detail": tableInfo[
                    "Milestone Payment And Detail"
                  ].filter(
                    (_, subGroupShellInfoIndex) =>
                      subGroupShellInfoIndex !== info.subGroupShellIndex,
                  ),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const deleteOtherPaymentSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Other Payment And Detail": tableInfo[
                    "Other Payment And Detail"
                  ].filter(
                    (_, subGroupShellInfoIndex) =>
                      subGroupShellInfoIndex !== info.subGroupShellIndex,
                  ),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const deleteRoyaltySubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.groupShellIndex) {
                return {
                  ...tableInfo,
                  "Royalty(LA&SC)": tableInfo["Royalty(LA&SC)"].filter(
                    (_, subGroupShellInfoIndex) =>
                      subGroupShellInfoIndex !== info.subGroupShellIndex,
                  ),
                };
              }

              return tableInfo;
            },
          ),
        },
      };
    };

    const deleteAdvisorSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Advisor Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Advisor Table"
          ]?.filter(
            (_, subGroupShellInfoIndex) =>
              subGroupShellInfoIndex !== info.subGroupShellIndex,
          ),
        },
      };
    };

    const deletePartnerASubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner A":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner A"
              ]?.filter(
                (_, subGroupShellInfoIndex) =>
                  subGroupShellInfoIndex !== info.subGroupShellIndex,
              ) ?? [],
          },
        },
      };
    };

    const deletePartnerBSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner B":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner B"
              ]?.filter(
                (_, subGroupShellInfoIndex) =>
                  subGroupShellInfoIndex !== info.subGroupShellIndex,
              ) ?? [],
          },
        },
      };
    };

    const deletePartnerCSubGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...(entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ] as PartnerAxisTable),
            "Partner C":
              entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"]?.[
                "Partner C"
              ]?.filter(
                (_, subGroupShellInfoIndex) =>
                  subGroupShellInfoIndex !== info.subGroupShellIndex,
              ) ?? [],
          },
        },
      };
    };

    if (info.subGroupShellKey === "Milestone Payment And Detail") {
      setEntryData(deleteMilestoneSubGroupShell());
      changeCurrentHistory({
        entryData: deleteMilestoneSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Other Payment And Detail") {
      setEntryData(deleteOtherPaymentSubGroupShell());
      changeCurrentHistory({
        entryData: deleteOtherPaymentSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Royalty(LA&SC)") {
      setEntryData(deleteRoyaltySubGroupShell());
      changeCurrentHistory({
        entryData: deleteRoyaltySubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Advisor") {
      setEntryData(deleteAdvisorSubGroupShell());
      changeCurrentHistory({
        entryData: deleteAdvisorSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Partner A") {
      setEntryData(deletePartnerASubGroupShell());
      changeCurrentHistory({
        entryData: deletePartnerASubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Partner B") {
      setEntryData(deletePartnerBSubGroupShell());
      changeCurrentHistory({
        entryData: deletePartnerBSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Partner C") {
      setEntryData(deletePartnerCSubGroupShell());
      changeCurrentHistory({
        entryData: deletePartnerCSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const handleClickToDeleteMilestoneSubGroupShell = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    if (focusStatus.type === "clear") {
      handleClearSubGroupShell();
    }
    if (focusStatus.type === "delete") {
      handleDeleteSubGroupShell();
    }
  };

  const handleKeyDownToDeleteMilestoneSubGroupShell = (
    event: KeyboardEvent,
  ) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearSubGroupShell();
    }
    if (focusStatus.type === "delete" && event.key === "Delete") {
      handleDeleteSubGroupShell();
    }
  };

  return {
    clickAddMilestoneSubGroupShellButton,
    handleClickToDeleteMilestoneSubGroupShell,
    handleKeyDownToDeleteMilestoneSubGroupShell,
  };
};
